import React, { useEffect, useState } from "react";
import { Inbox, TrendingUp, DollarSign, Users, Target, Award, Instagram, Facebook, Globe, Megaphone, Mail } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import CRMArtistDashboard from "@/pages/crm/CRMArtistDashboard";

const STAGE_LABELS = {
  new: "Nuovo",
  contacted: "Contattato",
  appointment: "Appuntamento",
  negotiation: "Trattativa",
  contract_sent: "Contratto inviato",
  won: "Vinto",
  lost: "Perso",
};

const SOURCE_LABELS = {
  "landing-1": "Landing 1",
  "landing-2": "Landing 2",
  contact: "Contatti",
  outreach: "Outreach",
};

const PLATFORM_ICON = {
  facebook: Facebook,
  fb: Facebook,
  instagram: Instagram,
  ig: Instagram,
  google: Globe,
  direct: Globe,
  email: Mail,
};

const platformIcon = (name) => {
  const key = String(name || "").toLowerCase();
  for (const [k, Icon] of Object.entries(PLATFORM_ICON)) {
    if (key.includes(k)) return Icon;
  }
  return Megaphone;
};

const Card = ({ icon: Icon, label, value, testId, accent }) => (
  <div className="border-2 border-[#0A0A0A] p-5 bg-[#FAFAF7]" data-testid={testId}>
    <div className="flex items-start justify-between">
      <Icon size={22} strokeWidth={1.5} className={accent ? "text-[#FF5A1F]" : "text-[#0A0A0A]"} />
    </div>
    <div className="mt-3 font-display text-4xl leading-none text-[#0A0A0A]">{value}</div>
    <div className="mt-2 text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">{label}</div>
  </div>
);

export default function CRMDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const isArtist = user?.role === "artist";

  useEffect(() => {
    if (isArtist) { setLoading(false); return; }
    const path = user?.role === "admin" ? "/crm/dashboard/admin" : "/crm/dashboard/me";
    api.get(path).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [user, isArtist]);

  // Artist gets a dedicated dashboard
  if (isArtist) return <CRMArtistDashboard />;

  if (loading) return <div className="p-8 text-[#0A0A0A]/50">Caricamento…</div>;
  if (!data) return <div className="p-8 text-[#0A0A0A]/50">Nessun dato disponibile.</div>;

  return (
    <div className="p-6 lg:p-10 max-w-7xl" data-testid="crm-dashboard">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Overview</div>
        <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">
          {user?.role === "admin" ? "Dashboard Admin" : "La mia dashboard"}
        </h1>
        <p className="text-[#0A0A0A]/60 mt-2">Benvenuto {user?.name || user?.email}.</p>
      </div>

      {/* Pipeline stages counts */}
      <div className="mb-8">
        <h2 className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]/50 mb-3">Pipeline</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
          {Object.entries(data.pipeline || {}).map(([stage, count]) => (
            <div key={stage} className="border-2 border-[#0A0A0A] p-4 bg-[#FAFAF7]" data-testid={`stage-${stage}`}>
              <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">{STAGE_LABELS[stage] || stage}</div>
              <div className="mt-1 font-display text-3xl leading-none">{count}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Global stats */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {user?.role === "admin" ? (
          <>
            <Card icon={Inbox} label="Lead totali" value={data.total_leads || 0} testId="admin-total-leads" accent />
            <Card icon={Award} label="Chiusi vinti" value={data.total_won || 0} testId="admin-won" />
            <Card icon={DollarSign} label="Revenue totale" value={`€${(data.total_revenue || 0).toLocaleString('it-IT')}`} testId="admin-revenue" accent />
            <Card icon={TrendingUp} label="Tasso conversione" value={`${data.conversion_rate || 0}%`} testId="admin-conversion" />
            <Card icon={Users} label="Venditori attivi" value={data.counts?.sellers || 0} testId="admin-sellers-count" />
            <Card icon={Target} label="Outreach attivi" value={data.counts?.outreach || 0} testId="admin-outreach-count" />
          </>
        ) : (
          <>
            <Card icon={Inbox} label="I miei lead" value={data.leads_total || 0} testId="me-total-leads" accent />
            <Card icon={Award} label="Vinti" value={data.leads_won || 0} testId="me-won" />
            <Card icon={DollarSign} label="Revenue generata" value={`€${(data.revenue || 0).toLocaleString('it-IT')}`} testId="me-revenue" />
            <Card icon={TrendingUp} label={`Commissione (${data.commission_pct || 0}%)`} value={`€${(data.commission_amount || 0).toLocaleString('it-IT')}`} testId="me-commission" accent />
          </>
        )}
      </div>

      {/* Per-seller (admin only) — Card layout instead of cramped table */}
      {user?.role === "admin" && data.per_seller && data.per_seller.length > 0 && (
        <div className="mt-10">
          <h2 className="font-display text-3xl uppercase text-[#0A0A0A] mb-4">Performance venditori</h2>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {data.per_seller.map((s) => (
              <SellerCard key={s.id} seller={s} />
            ))}
          </div>
        </div>
      )}

      {/* UTM analytics (admin only) */}
      {user?.role === "admin" && (
        <div className="mt-12 space-y-8">
          <div>
            <div className="flex items-baseline justify-between mb-4 flex-wrap gap-2">
              <h2 className="font-display text-3xl uppercase text-[#0A0A0A]">Attribution & Tracking</h2>
              <span className="text-xs text-[#0A0A0A]/50">Da dove arrivano i tuoi lead</span>
            </div>

            <div className="grid gap-6 lg:grid-cols-2">
              <UtmBlock title="Fonte del traffico" subtitle="utm_source" data={data.by_utm_source} testIdPrefix="utm-source" />
              <UtmBlock title="Piattaforma" subtitle="utm_platform" data={data.by_utm_platform} testIdPrefix="utm-platform" withIcon />
              <UtmBlock title="Campagna" subtitle="utm_campaign" data={data.by_utm_campaign} testIdPrefix="utm-campaign" />
              <UtmBlock title="Mezzo" subtitle="utm_medium" data={data.by_utm_medium} testIdPrefix="utm-medium" />
            </div>
          </div>

          {/* Lead sources by page (landing 1 vs landing 2 vs contact vs outreach) */}
          {data.by_source && (
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]/50 mb-3">Pagina d'ingresso</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(data.by_source).map(([src, count]) => (
                  <div key={src} className="border-2 border-[#0A0A0A] p-4 bg-[#FAFAF7]" data-testid={`source-${src}`}>
                    <div className="text-[10px] uppercase tracking-widest text-[#FF5A1F] font-bold">{SOURCE_LABELS[src] || src}</div>
                    <div className="mt-1 font-display text-3xl">{count}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const SellerCard = ({ seller }) => (
  <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-5" data-testid={`seller-row-${seller.id}`}>
    <div className="flex items-start gap-3 mb-4">
      <div className="shrink-0 w-11 h-11 border-2 border-[#0A0A0A] bg-[#FF5A1F] text-[#FAFAF7] font-display text-lg uppercase flex items-center justify-center">
        {(seller.name || "?").slice(0, 1).toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <h3 className="font-bold text-lg text-[#0A0A0A] truncate">{seller.name}</h3>
        <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 mt-0.5">
          Commissione {seller.commission_pct || 0}%
        </div>
      </div>
    </div>
    <div className="grid grid-cols-2 gap-3 text-sm border-t border-[#0A0A0A]/10 pt-4">
      <Stat label="Lead" value={seller.leads_total} />
      <Stat label="Vinti" value={seller.leads_won} />
      <Stat label="Conversione" value={`${seller.conversion_rate}%`} />
      <Stat label="Revenue" value={`€${seller.revenue.toLocaleString('it-IT')}`} />
    </div>
    <div className="mt-4 pt-3 border-t border-[#0A0A0A]/10 flex items-baseline justify-between">
      <span className="text-[10px] uppercase tracking-widest font-bold text-[#0A0A0A]/60">Commissione maturata</span>
      <span className="font-display text-2xl text-[#FF5A1F]">€{seller.commission_amount.toLocaleString('it-IT')}</span>
    </div>
  </div>
);

const Stat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50 font-bold">{label}</div>
    <div className="mt-0.5 font-display text-xl text-[#0A0A0A] leading-none">{value}</div>
  </div>
);

const UtmBlock = ({ title, subtitle, data, testIdPrefix, withIcon }) => {
  const rows = (data || []).filter((r) => r.count > 0);
  const total = rows.reduce((s, r) => s + r.count, 0);
  if (!rows.length) {
    return (
      <div className="border-2 border-[#0A0A0A]/20 bg-[#FAFAF7] p-5" data-testid={`${testIdPrefix}-empty`}>
        <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">{title}</div>
        <code className="text-[10px] text-[#0A0A0A]/40 uppercase">{subtitle}</code>
        <div className="mt-4 text-xs text-[#0A0A0A]/50 italic">
          Ancora nessun dato tracciato. I lead che arrivano dagli ads con parametri UTM appariranno qui.
        </div>
      </div>
    );
  }
  return (
    <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] p-5" data-testid={`${testIdPrefix}-block`}>
      <div className="flex items-baseline justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">{title}</div>
          <code className="text-[10px] text-[#0A0A0A]/40 uppercase">{subtitle}</code>
        </div>
        <span className="text-xs text-[#0A0A0A]/50">Totale {total}</span>
      </div>
      <ul className="mt-4 space-y-2">
        {rows.map((r) => {
          const pct = total ? (r.count / total) * 100 : 0;
          const Icon = withIcon ? platformIcon(r.key) : null;
          return (
            <li key={r.key} data-testid={`${testIdPrefix}-row-${r.key}`}>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="flex items-center gap-2 font-bold uppercase tracking-widest truncate min-w-0">
                  {Icon && <Icon size={13} className="text-[#FF5A1F] shrink-0" />}
                  <span className="truncate">{r.key || "direct"}</span>
                </span>
                <span className="text-[#0A0A0A]/60 shrink-0">{r.count} · {pct.toFixed(0)}%</span>
              </div>
              <div className="h-1.5 bg-[#0A0A0A]/10 relative">
                <div className="absolute inset-y-0 left-0 bg-[#FF5A1F]" style={{ width: `${pct}%` }} />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
